import React, { RefObject } from "react";
import { motion, useDragControls, useReducedMotion } from "framer-motion";
import { useIsMobile } from "@/hooks/useIsMobile";

const TINTS: Record<string, string> = {
  white: "bg-white",
  lavender: "bg-lavender",
  mint: "bg-mint",
  butter: "bg-butter",
  pink: "bg-lnpink",
};

interface OSWindowProps {
  id: string;
  title: string;
  tint?: string;
  className?: string;
  onClose?: () => void;
  onMinimize?: () => void;
  onFocus?: () => void;
  z?: number;
  constraintsRef?: RefObject<Element>;
  isStatic?: boolean;
  children: React.ReactNode;
}

export const OSWindow = ({
  id, title, tint = "white", className = "", onClose, onMinimize, onFocus,
  z = 10, constraintsRef, isStatic = false, children,
}: OSWindowProps) => {
  const isMobile = useIsMobile();
  const reduced = useReducedMotion();
  const controls = useDragControls();
  const draggable = !isMobile && !isStatic;

  return (
    <motion.div
      data-testid={`window-${id}`}
      drag={draggable}
      dragControls={controls}
      dragListener={false}
      dragMomentum={false}
      dragElastic={0.05}
      dragConstraints={constraintsRef}
      initial={reduced ? false : { opacity: 0, scale: 0.94, y: 16 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={reduced ? undefined : { opacity: 0, scale: 0.94, y: 10, transition: { duration: 0.18 } }}
      transition={{ type: "spring", stiffness: 260, damping: 26 }}
      onPointerDown={onFocus}
      style={{ zIndex: z }}
      className={`os-window ${className}`}
    >
      <div
        className="os-titlebar"
        data-testid={`window-titlebar-${id}`}
        onPointerDown={(e) => {
          if (draggable) controls.start(e);
        }}
        style={{ cursor: draggable ? "grab" : "default" }}
      >
        <span className="flex items-center gap-2 truncate">
          <span className="inline-block h-2.5 w-2.5 shrink-0 bg-ink" aria-hidden />
          <span className="truncate">{title}</span>
        </span>
        <span className="flex shrink-0 gap-1.5">
          {onMinimize && (
            <button aria-label="Minimize window" data-testid={`window-minimize-${id}`} className="os-ctrl" onClick={onMinimize}>
              _
            </button>
          )}
          {onClose && (
            <button aria-label="Close window" data-testid={`window-close-${id}`} className="os-ctrl" onClick={onClose}>
              ×
            </button>
          )}
        </span>
      </div>
      <div className={TINTS[tint] || "bg-white"}>{children}</div>
    </motion.div>
  );
};
